// guideMessages.ts — the guide-presence content map (Task 1A). Clinician-editable:
// captions and quote pools live here as data, so wording changes need NO code change.
// Quotes are VERBATIM from Craig's pacing-and-persistence set, so the guide speaks
// in his voice. Clips are referenced by clipId → /guide/<clipId>.webp (built by
// scripts/build-guide-clips.cjs). The guide is an ally beside the user, never a
// monitor — nothing here may read as evaluation, pressure, or "where have you been?".

export type GuideHook =
  | 'H1_welcome'
  | 'H2_return'
  | 'H3_reallyHard'
  | 'H4_actBoundary'
  | 'H5_mastery'
  | 'H6_checkinInvite'
  | 'H7_overPacing'
  | 'H8_milestone'
  | 'H9_toolUnlocked'
  | 'H10_toolkitInvite'
  | 'H11_encouragement';

export type GuideFreq = 'once' | 'perReturn' | 'perTrigger' | 'perBoundary' | 'oncePerSession' | 'light';
export type ActId = 'act1' | 'act2' | 'act3';

export interface GuideQuote {
  id: string;
  text: string;
  theme: string;
  actScope?: ActId;
}

export interface GuideMessage {
  clipId: string;
  caption: string;
  quotePool?: GuideQuote[];
  quoteByAct?: Record<ActId, GuideQuote[]>;
  freq: GuideFreq;
  /** Weighted hooks always show a quote; H6 stays light (occasional). */
  showQuote: 'always' | 'occasional' | 'never';
}

/** Days inactive before a return counts as "away" (H2). Clinician-tunable. */
export const RETURN_AWAY_DAYS = 5;

/** H8 milestone: every chapter gets the quiet "chapter complete" marker; Craig's
 *  well-done face appears every Nth chapter on top of it. Clinician-tunable. */
export const MILESTONE_FACE_EVERY_N = 2;

/** clipId → served webp path. Relative (no leading slash) to match the lesson-image
 *  convention, so it resolves on native Capacitor as well as the web. */
export const clipUrl = (clipId: string): string => `guide/${clipId}.webp`;

export const GUIDE_MESSAGES: Record<GuideHook, GuideMessage> = {
  H1_welcome: {
    clipId: 'welcome',
    caption: "You're safe here. There's no rush, and nothing to catch up on — we'll go at your pace.",
    freq: 'once',
    showQuote: 'always',
    quotePool: [
      { id: 'h1-pacing-1', theme: 'pacing', text: "There's a reason that ReNurture was created the way it's created — it's self-paced. And it is not a failure to slow down. It is the total opposite." },
      { id: 'h1-longjourney-1', theme: 'long journey', text: 'The ReNurture programme is here and will be here, no matter how long it takes you and what breaks you need.' },
    ],
  },

  H2_return: {
    clipId: 'welcome-back',
    caption: 'Welcome back. Nothing to catch up on — just pick up wherever feels right.',
    freq: 'perReturn',
    showQuote: 'always',
    quotePool: [
      { id: 'h2-turningup-1', theme: 'turning up', text: 'In rehab, the people who recover — the people who are going to walk again — are the people who get up every day to try and take steps, no matter what happened the day before.' },
      { id: 'h2-linear-1', theme: "recovery isn't linear", text: "You'll develop an insight that you may need to go back and repeat a step. That may need to happen many times — and still happens for me to this day." },
      { id: 'h2-longjourney-1', theme: 'long journey', text: 'The ReNurture programme is here and will be here, no matter how long it takes you and what breaks you need.' },
      { id: 'h2-turningup-2', theme: 'turning up', text: "Sometimes it'll take longer to get up, but we know we are going to. We notice that we get up. We pay less attention to the fact that we fall down." },
    ],
  },

  H3_reallyHard: {
    clipId: 'self-care',
    caption: "Let's be gentle today. Nothing's owed, being here is enough.",
    freq: 'perTrigger',
    showQuote: 'always',
    quotePool: [
      { id: 'h3-pacing-1', theme: 'pacing', text: 'It is actually okay to pause the programme or to stop. It is okay to realise that this work might be something you need to do, but now might not be the time.' },
      { id: 'h3-patience-1', theme: 'patience', text: "When we do work on ourselves, we also want to be kind to ourselves. If we're noticing this is upsetting us or feels too difficult, take a break. This is not meant to be a trial by fire." },
      { id: 'h3-pacing-2', theme: 'pacing', text: "There's a reason that ReNurture was created the way it's created — it's self-paced. And it is not a failure to slow down. It is the total opposite." },
      { id: 'h3-pacing-3', theme: 'pacing', text: "When we find ourselves unable to access skilled support, we can slow down the process. We can spend time with a journal, we can spend time learning self-compassion — we can lean into this incredibly lightly. We don't have to push hard." },
    ],
  },

  H4_actBoundary: {
    clipId: 'act-complete',
    caption: "That was real work. Take the time you need before the next part — it'll be here when you're ready.",
    freq: 'perBoundary',
    showQuote: 'always',
    quoteByAct: {
      act1: [
        { id: 'h4-act1-longjourney', theme: 'long journey', actScope: 'act1', text: 'This is incredibly complex to hold onto and to transform inside your own worldview. To wake up, to recognise reality, to rebuild trust takes time.' },
      ],
      act2: [
        { id: 'h4-act2-persistence', theme: 'persistence', actScope: 'act2', text: 'Well done for continuing to this point in the programme. I genuinely feel like saying well done at almost every step, because it is a lot of work. Please repeat this section as many times as you need to.' },
        { id: 'h4-act2-longjourney', theme: 'long journey', actScope: 'act2', text: "It's going to get harder before it gets easier. And it's not fast. Sometimes it'll feel like you've gone forwards and then backwards — it won't feel like a smooth journey." },
      ],
      act3: [
        { id: 'h4-act3-persistence', theme: 'persistence', actScope: 'act3', text: 'It is the effort to source our own love, to meet our own needs that creates the life we want. And it is 100% worth it. It is what growth feels like.' },
      ],
    },
  },

  H5_mastery: {
    clipId: 'skill-learned',
    caption: "You're becoming someone who can steady yourself. That's the work — and you're doing it.",
    // perTrigger: the real cap is the one-shot `guideMasteryDue` flag (armed when a
    // key-skill chapter completes, consumed by GuideFlowBeat). A second key skill in
    // the same session SHOULD be celebrated, so no session cap here.
    freq: 'perTrigger',
    showQuote: 'always',
    quotePool: [
      { id: 'h5-persistence-1', theme: 'persistence', text: 'Every time you try these steps, you are working on creating a better version of yourself that loves yourself through actually enacting meeting your own needs. That has to be noticed and celebrated every time, no matter the outcome.' },
      { id: 'h5-persistence-2', theme: 'persistence', text: "If humans only worked on things that they won at every time they do them, we'd never get out of bed." },
      { id: 'h5-babysteps-1', theme: 'baby steps', text: "Baby steps are brilliant. Very small realisations build up the momentum — it's like a rolling snowball. Things that start small can get bigger and bigger." },
      { id: 'h5-babysteps-2', theme: 'baby steps', text: 'As a first step towards really processing and understanding these emotions in our lives, all we have to do is start working on noticing them. That\'s it. That is the first step. Nothing else. We don\'t need to try and fix them.' },
    ],
  },

  H6_checkinInvite: {
    clipId: 'self-care',
    caption: "There's a check-in here if it'd help today — entirely up to you.",
    freq: 'light',
    showQuote: 'occasional',
    quotePool: [
      { id: 'h6-turningup-1', theme: 'turning up', text: 'I talk a lot in the programme about turning up for ourselves as an expression of self-love. That is how we most love ourselves: to give ourselves what we need.' },
      { id: 'h6-turningup-2', theme: 'turning up', text: 'It is the act of turning up for yourself, day after day, that will change how you feel about yourself.' },
    ],
  },

  // H7 — over-pacing / bingeing. NOT a brake: it never blocks. It rides on the
  // existing reflection-skip run (3 of the last 5 advanced fast), surfacing once
  // per session as a warm Today tile that invites a little space. Reuses Craig's
  // pacing quotes (verbatim, shared with H3). Suppressed while paused / "my own way".
  H7_overPacing: {
    clipId: 'invite-to-slowdown',
    caption: 'Lovely to see you so engaged today. These settle better with a little space — maybe come back to it tomorrow?',
    // perTrigger: capped by the one-shot `guideOverPacingDue` flag, not a session count.
    freq: 'perTrigger',
    showQuote: 'always',
    quotePool: [
      { id: 'h7-pacing-1', theme: 'pacing', text: 'It is actually okay to pause the programme or to stop. It is okay to realise that this work might be something you need to do, but now might not be the time.' },
      { id: 'h7-patience-1', theme: 'patience', text: "When we do work on ourselves, we also want to be kind to ourselves. If we're noticing this is upsetting us or feels too difficult, take a break. This is not meant to be a trial by fire." },
      { id: 'h7-pacing-2', theme: 'pacing', text: "There's a reason that ReNurture was created the way it's created — it's self-paced. And it is not a failure to slow down. It is the total opposite." },
      { id: 'h7-pacing-3', theme: 'pacing', text: "When we find ourselves unable to access skilled support, we can slow down the process. We can spend time with a journal, we can spend time learning self-compassion — we can lean into this incredibly lightly. We don't have to push hard." },
    ],
  },

  // H8 — chapter milestone. A light "you did this" affirmation after finishing a
  // chapter. No quote. The face shows only occasionally (every Nth chapter) and
  // NEVER at an act boundary, where H4 owns the moment. The caller gates frequency.
  H8_milestone: {
    clipId: 'well-done',
    caption: "You finished a chapter. That's real, steady work, let it land before you carry on.",
    freq: 'perTrigger',
    showQuote: 'never',
  },

  // H9 — the MERGED "skill learned + tool unlocked" moment. When a key-skill lesson
  // completes it both celebrates the mastery AND announces the new toolkit exercise,
  // as a single Craig tile (skill-learned image). The caller appends the tool title.
  H9_toolUnlocked: {
    clipId: 'toolkit',
    caption: 'Well done. You have just learned a new skill.',
    freq: 'perTrigger',
    showQuote: 'never',
  },

  // H10 — a one-time invitation to actually use the toolkit + library when things
  // are hard (not only in crisis). Shown once, around the first tool unlock.
  H10_toolkitInvite: {
    clipId: 'self-care',
    caption: 'Explore your toolkit and learn what might be useful, for when you need it. Some of these work best when you practice them while you are calm, so have a look through them all. The rest unlock as you are taken on the journey, revealing themselves as you learn the psychology that helps you get the most from each one.',
    freq: 'perTrigger',
    showQuote: 'never',
  },

  // H11 — "A word from Craig": a gentle, occasional founder note on an ordinary day,
  // tied to nothing but showing up. Uses the composed quote-card image; the quote
  // carries the weight. Light, capped, rotating; yields to any weighted hook. Cadence
  // (every-N-sessions, a 3-day floor, suppressed while paused, yield) is enforced by
  // the caller (HomeScreen), not here. Tap to dismiss.
  H11_encouragement: {
    clipId: 'quote-card',
    caption: 'A word from Craig.',
    freq: 'perTrigger',
    showQuote: 'always',
    quotePool: [
      { id: 'h11-turningup-1', theme: 'turning up', text: 'It is the act of turning up for yourself, day after day, that will change how you feel about yourself.' },
      { id: 'h11-turningup-2', theme: 'turning up', text: 'I talk a lot in the programme about turning up for ourselves as an expression of self-love. That is how we most love ourselves: to give ourselves what we need.' },
      { id: 'h11-persistence-1', theme: 'persistence', text: 'Accept that you will fail many times over on your journey to love, but trust that when we turn up again and again to learn and we meet our needs and connect with our own value, we will also learn to trust ourselves.' },
      { id: 'h11-persistence-2', theme: 'persistence', text: 'It is the effort to source our own love, to meet our own needs that creates the life we want. And it is 100% worth it. It is what growth feels like.' },
      { id: 'h11-turningup-3', theme: 'turning up', text: "Sometimes it'll take longer to get up, but we know we are going to. We notice that we get up. We pay less attention to the fact that we fall down." },
      { id: 'h11-babysteps-1', theme: 'baby steps', text: 'Baby steps are brilliant. Very small realisations build up the momentum, like a rolling snowball.' },
    ],
  },
};

/** How many ordinary sessions between encouragement (H11) showings. Clinician-tunable. */
export const ENCOURAGEMENT_EVERY_N_SESSIONS = 3;

/** The merged H9 caption: the mastery line plus the named tool, built at fire time
 *  (so the message celebrates the skill AND names the new tool, while the base copy
 *  stays editable above). */
export const toolUnlockedCaption = (toolTitle: string): string =>
  `${GUIDE_MESSAGES.H9_toolUnlocked.caption} “${toolTitle}” is now in your toolkit, ready whenever you need it.`;
